import _ from "underscore";
import React from "react";
import { ResponsivePie } from "@nivo/pie";
import { I18n } from "i18n-js";
import * as locales from "../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import MinigraphCard from "./minigraphCard";
import AnalyticsLoading from "../analyticsLoading";
import AnalyticsError from "../analyticsError";

interface BonusesCardProps {
  devMode: boolean;
  data: any;
  fetchBonusesData: any;
  colors: any[];
  bonusesGiven: number;
  bonusesReceived: number;
  pointsGiven: number;
  uniqueHashtags: number;
  requestStatus: string;
}

const MINIMUM_DATA_LENGTH = 1;

export default class BonusesCard extends React.Component<
  BonusesCardProps,
  any
> {
  componentDidMount() {
    if (this.props.requestStatus === "loading") {
      this.props.fetchBonusesData();
    }
  }

  render() {
    return (
      <MinigraphCard
        devMode={this.props.devMode}
        title={this.getTitle()}
        url={this.getUrl()}
        chartName="bonuses"
      >
        {this.renderBonusesCardContent()}
      </MinigraphCard>
    );
  }

  renderBonusesCardContent = () => {
    if (this.props.requestStatus === "error") {
      return (
        <AnalyticsError
          error={i18n.translate("analyticsv2.something_went_wrong")}
        />
      );
    }
    if (this.props.requestStatus === "loading") {
      return <AnalyticsLoading />;
    }
    if (
      this.props.bonusesGiven + this.props.bonusesReceived <
      MINIMUM_DATA_LENGTH
    ) {
      return (
        <AnalyticsError
          error={i18n.translate("analyticsv2.bonuses_card.not_enough_data")}
        />
      );
    }
    if (this.props.requestStatus === "success") {
      return (
        <>
          {this.renderHeaderStats()}
          {this.renderBonusesGraph()}
        </>
      );
    }

    return null;
  };

  renderHeaderStats = () => {
    return (
      <div className="bonuses-card-header-container">
        <div className="header-cell">
          {this.renderDataCell(
            this.props.bonusesGiven,
            i18n.translate("analyticsv2.bonuses_card.bonuses_given")
          )}
        </div>
        <div className="header-cell middle">
          {this.renderDataCell(
            this.props.bonusesReceived,
            i18n.translate("analyticsv2.bonuses_card.bonuses_received")
          )}
        </div>
        <div className="header-cell">
          {this.renderDataCell(
            this.props.pointsGiven,
            i18n.translate("analyticsv2.bonuses_card.points_given")
          )}
        </div>
      </div>
    );
  };

  renderDataCell = (num, label) => {
    return (
      <>
        <div className="header-number">{num}</div>
        <div className="header-subheader">{label}</div>
      </>
    );
  };

  renderBonusesGraph = () => {
    if (this.props.uniqueHashtags === 0) {
      return null;
    }
    return (
      <div className="bonuses-graph-container">
        {this.renderUniqueHashtags()}
        <ResponsivePie
          animate
          borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
          borderWidth={0}
          cornerRadius={0}
          colors={this.props.colors}
          data={this.getChartData(this.props.data)}
          enableRadialLabels={false}
          enableSlicesLabels={false}
          innerRadius={0.6}
          margin={{
            top: 20,
            right: 80,
            bottom: 20,
            left: -100,
          }}
          motionDamping={15}
          motionStiffness={90}
          padAngle={2}
          legends={[
            {
              anchor: "right",
              direction: "column",
              translateX: -10,
              translateY: 0,
              itemWidth: 100,
              itemHeight: 30,
              itemTextColor: "#48545B",
              symbolSize: 18,
              symbolShape: "circle",
            },
          ]}
          theme={{
            tooltip: {
              basic: {
                color: "#fefefe",
                fontWeight: "bold",
              },
              container: {
                background: "#333",
                opacity: 0.85,
                padding: 8,
              },
            },
          }}
        />
      </div>
    );
  };

  renderUniqueHashtags = () => {
    return (
      <div className="unique-hashtags">
        {this.renderDataCell(
          this.props.uniqueHashtags,
          i18n.translate("analyticsv2.bonuses_card.unique_hashtags_used")
        )}
      </div>
    );
  };

  getChartData = (totalsByHashtag) => {
    const transformedData = this.transformData(totalsByHashtag);
    const sortedData = this.sortDataDescending(transformedData);
    const manipulatedData = this.moveOtherToEnd(sortedData);
    const groupedData = this.groupData(manipulatedData);
    return groupedData;
  };

  transformData = (data) => {
    // Transforms data to be consumed by Nivo.
    const transformedData = Object.keys({ ...data }).map((key) => {
      return {
        id: key,
        label: key,
        value: data[key],
      };
    });

    return transformedData;
  };

  sortDataDescending = (data) => {
    // Sorts data by value in descending order.
    const sortedData = _.sortBy([...data], "value");
    return sortedData.reverse();
  };

  moveOtherToEnd = (data) => {
    // Moves 'Other' hashtag to the end of the array, if there is one.
    const manipulatedData = [...data];
    const otherIndex = _.findIndex(data, (item: { id: string }) => {
      return item.id === "other";
    });
    if (otherIndex === -1) {
      return manipulatedData;
    }
    const otherItem = manipulatedData.splice(otherIndex, 1)[0];
    manipulatedData.push(otherItem);

    return manipulatedData;
  };

  groupData = (data) => {
    // Combines items beyond the first 4 into a single 'other' category.
    const groupedData = [...data];
    if (groupedData.length <= 5) {
      return groupedData;
    }
    const itemsToCombine = groupedData.splice(4);
    const sum = itemsToCombine.reduce((acc, item) => {
      return acc + item.value;
    }, 0);

    groupedData.push({
      id: "other",
      label: "other",
      value: sum,
    });

    return groupedData;
  };

  getTitle = () => {
    return "Recognition";
  };

  getUrl = () => {
    return "/analytics/bonus_totals/summary";
  };
}
